import { computed, type ComputedRef } from 'vue'

import type { ReportColumn } from '../models/column.types'
import type { ReportLevel, ReportLevelSource } from '../models/level.types'
import { useReportLevelStore } from '../stores/reportLevelStore'

import { useMetricCatalog } from './useMetricCatalog'
import { useReportAccess } from './useReportAccess'

import { useWorkspace } from '@/features/account'

const LEVEL_ORDER: Readonly<Record<string, number>> = {
  campaign: 1,
  adgroup: 2,
  ad: 3,
  creative_image: 4,
  creative_video: 5,
}

export interface UseLevelMetadata {
  levels: ComputedRef<ReportLevel[]>
  formatLevels: (workspaceId?: string | number | null) => ReportLevel[]
  levelByPartition: (
    partitionName: string,
    workspaceIds?: readonly (string | number)[],
  ) => ReportLevel | null
  levelByLevel: (level: string, workspaceId?: string | number | null) => ReportLevel | null
  runtimeMetricFields: (partitionName: string) => string[]
}

/** Join bootstrap level groups with module 08's versioned, typed column catalog. */
export function useLevelMetadata(): UseLevelMetadata {
  const store = useReportLevelStore()
  const catalog = useMetricCatalog()
  const reports = useReportAccess()
  const { workspaceId } = useWorkspace()

  function column(field: string, numeric: boolean): ReportColumn {
    const found = catalog.getColumn(field)
    return found
      ? { ...found, numeric: found.numeric ?? numeric }
      : { field, header: catalog.metricLabel(field), numeric }
  }

  function format(source: ReportLevelSource): ReportLevel {
    return {
      level: source.level,
      partition_name: source.partition_name,
      report_id: source.report_id,
      name: catalog.metricLabel(source.level),
      primary_fields: [...source.data.primary_fields],
      tableMetrics: source.data.metrics.map((field) => column(field, true)),
      tableDimensions: source.data.dimensions.map((field) => column(field, false)),
      selectedReport: reports.reportById(source.report_id),
    }
  }

  function formatLevels(
    selectedWorkspaceId: string | number | null = workspaceId.value ?? null,
  ): ReportLevel[] {
    const unique = new Map<string, ReportLevel>()
    for (const source of store.levelsForWorkspace(selectedWorkspaceId)) {
      const level = format(source)
      unique.set(`${level.report_id}:${level.partition_name}`, level)
    }
    return [...unique.values()].sort(
      (left, right) =>
        (LEVEL_ORDER[left.level] ?? 99) - (LEVEL_ORDER[right.level] ?? 99) ||
        left.name.localeCompare(right.name),
    )
  }

  function levelByPartition(
    partitionName: string,
    workspaceIds?: readonly (string | number)[],
  ): ReportLevel | null {
    const ids = workspaceIds?.length ? workspaceIds : [workspaceId.value ?? null]
    for (const id of ids) {
      const match = formatLevels(id).find((level) => level.partition_name === partitionName)
      if (match) return match
    }
    return null
  }

  function levelByLevel(
    levelName: string,
    selectedWorkspaceId: string | number | null = workspaceId.value ?? null,
  ): ReportLevel | null {
    return formatLevels(selectedWorkspaceId).find((level) => level.level === levelName) ?? null
  }

  return {
    levels: computed(() => formatLevels()),
    formatLevels,
    levelByPartition,
    levelByLevel,
    runtimeMetricFields: (partitionName) =>
      store.runtimeMetricFields(workspaceId.value, partitionName),
  }
}
